"use client";

import type { LogAttributes, LogSeverityLevel } from "posthog-js";
import { env } from "~/env.mjs";
import { hasBrowserAnalyticsConsent } from "~/lib/consent-region";
import { isPostHogBrowserEnabled, posthog } from "~/lib/posthog/client";
import {
  addPostHogClientBreadcrumb,
  capturePostHogClientException,
} from "~/lib/posthog/error-tracking";
import { shouldCapturePostHogClientSignal } from "~/lib/posthog/client-rate-limit";

type MonitoringLevel = "info" | "warning" | "error";

function toPostHogLogLevel(level: MonitoringLevel): LogSeverityLevel {
  if (level === "warning") {
    return "warn";
  }

  return level;
}

function getBrowserLogAttributes(
  properties?: Record<string, unknown>,
): LogAttributes {
  return {
    "service.name": "resimo-probleme-web",
    "deployment.environment": env.NEXT_PUBLIC_VERCEL_ENV ?? "unknown",
    "service.runtime": "browser",
    page_path:
      typeof window === "undefined" ? undefined : window.location.pathname,
    page_location:
      typeof window === "undefined" ? undefined : window.location.href,
    ...properties,
  };
}

function getMonitoringLevel(
  properties?: Record<string, unknown>,
): MonitoringLevel {
  if (properties?.level === "warning" || properties?.level === "error") {
    return properties.level;
  }

  return "info";
}

function captureTechnicalMessage(
  message: string,
  level: MonitoringLevel,
  properties?: Record<string, unknown>,
): void {
  if (!hasBrowserAnalyticsConsent()) {
    return;
  }

  if (!isPostHogBrowserEnabled()) {
    return;
  }

  if (!shouldCapturePostHogClientSignal(`log:${level}:${message}`)) {
    return;
  }

  posthog.captureLog({
    body: message,
    level: toPostHogLogLevel(level),
    attributes: getBrowserLogAttributes(properties),
  });
}

export function captureException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  capturePostHogClientException(error, properties);
}

export function captureMessage(
  message: string,
  properties?: Record<string, unknown>,
): void {
  captureTechnicalMessage(message, getMonitoringLevel(properties), properties);
}

export function addBreadcrumb(properties: Record<string, unknown>): void {
  addPostHogClientBreadcrumb(properties);
}

export const logger = {
  info(message: string, properties?: Record<string, unknown>): void {
    captureTechnicalMessage(message, "info", properties);
  },
  warn(message: string, properties?: Record<string, unknown>): void {
    captureTechnicalMessage(message, "warning", properties);
  },
  error(message: string, properties?: Record<string, unknown>): void {
    captureTechnicalMessage(message, "error", properties);
  },
};
